import { useAtomValue, useSetAtom } from 'jotai';
import {
  DocumentGetData,
  endpointDocumentGET,
} from '~/api/def/endpoints/documents';
import {
  endpointFundGET,
  FundDocument,
} from '~/api/def/endpoints/fundEndpoint';
import {
  documentCacheAtom,
  loadDocumentsAtom,
} from '~/atoms/document/documentsCacheAtoms';
import { existingFundSetAtom, fundHierarchyAtom } from '~/atoms/fundAtoms';
import { fundsToHierarchy } from '~/lib/fund/fundsToHierarchy';

export const useDocumentCache = () => {
  const loadDocuments = useSetAtom(loadDocumentsAtom);
  const existingFundSet = useAtomValue(existingFundSetAtom);
  const documentCache = useAtomValue(documentCacheAtom);
  const setFundHierarchy = useSetAtom(fundHierarchyAtom);

  const transformToFundDocument = (doc: DocumentGetData): FundDocument => {
    return {
      id: doc.id,
      originalName: doc.originalName,
      internalUrl: doc.internalUrl,
      extractionStatus: doc.extractionStatus,
      liveness: doc.liveness,
      restated: doc.restated === 'true',
      type: doc.type,
      investorName: '', // These fields will be populated by the fund documents endpoint
      fundId: doc.fundId,
      portfolioCompanyName: '',
      portfolioCompanyId: doc.portfolioCompanyId,
      updatedAt: doc.updatedAt,
      createdAt: new Date(doc.createdAt),
      source: doc.source,
      admissionDate: null, // Since we don't have this from the document endpoint, we'll set it to null
    };
  };

  const getDocument = async (documentId: string) => {
    if (documentCache.has(documentId)) {
      return documentCache.get(documentId);
    }

    // Fallback to loading all documents if the direct fetch fails
    let newFunds: string[] = [];
    if (existingFundSet.size === 0) {
      const fundsResponse = await endpointFundGET();
      if (fundsResponse?.success === 1 && 'data' in fundsResponse) {
        const { hierarchy } = fundsToHierarchy(fundsResponse.data);
        setFundHierarchy(hierarchy);
        newFunds = hierarchy.flatMap((j) => j.funds.map((f) => f.fundId));
      }
    }

    try {
      const response = await endpointDocumentGET(documentId);
      if (response?.success === 1 && 'data' in response) {
        const document = response.data;
        const fundDocument = transformToFundDocument(document);
        documentCache.set(documentId, fundDocument);
        return fundDocument;
      }
    } catch (error) {
      console.error('Error fetching document:', error);

      const fundsToProcess =
        existingFundSet.size > 0 ? existingFundSet : newFunds;

      await Promise.all(
        Array.from(fundsToProcess).map((fundId: string) =>
          loadDocuments(fundId),
        ),
      );

      if (documentCache.has(documentId)) {
        return documentCache.get(documentId);
      }
    }
  };

  return {
    getDocument,
  };
};
